// BillingView â€” admin de plataforma vÃª planos, subscriptions, invoices

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Switch } from "@app/ui/components/switch";
import { openAlert } from "./ConfirmPromptModal";
import {
  AlertCircle,
  Ban,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  DollarSign,
  Edit,
  Loader2,
  Plus,
  Search,
  Tag,
  Trash2,
  Users,
  X
} from "lucide-react";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface Plan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  priceCents: number;
  period: string;
  defaultBillingDay: number;
  features: Record<string, unknown>;
  isActive: boolean;
  isDefault: boolean;
}
interface Subscription {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  planId: string;
  planSlug: string;
  planName: string;
  priceCents: number;
  status: string;
  billingDayOfMonth: number;
  startedAt: string;
  endsAt: string | null;
  nextInvoiceAt: string | null;
}
interface Invoice {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  subscriptionId: string;
  amountCents: number;
  dueAt: string;
  paidAt: string | null;
  status: string;
  paidByMethod: string | null;
  createdAt: string;
}

const tab = signal<"plans" | "subscriptions" | "invoices">("subscriptions");
const plans = signal<Plan[]>([]);
const subs = signal<Subscription[]>([]);
const invoices = signal<Invoice[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterStatus = signal<string>("all");
const editingPlan = signal<Plan | null>(null);
const editingSub = signal<Subscription | null>(null);
const creatingPlan = signal<boolean>(false);

function brl(cents: number): string {
  return "R$ " + (cents / 100).toFixed(2).replace(".", ",");
}

async function callApi<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body as { error?: { message?: string } } | null;
    throw new Error(err?.error?.message ?? `HTTP ${res.status}`);
  }
  return body as T;
}

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [pRes, sRes, iRes] = await Promise.all([
      callApi<{ plans: Plan[] }>("/api/manager/billing?view=plans"),
      callApi<{ subscriptions: Subscription[] }>("/api/manager/billing?view=subscriptions"),
      callApi<{ invoices: Invoice[] }>("/api/manager/billing?view=invoices")
    ]);
    plans.value = pRes.plans;
    subs.value = sRes.subscriptions;
    invoices.value = iRes.invoices;
    phase.value = "ready";
  } catch (e) {
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function BillingView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregandoâ€¦
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {errMsg.value}
      </div>
    );
  return (
    <div class="space-y-4">
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 p-2">
        {(["subscriptions", "plans", "invoices"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              tab.value = t;
              search.value = "";
            }}
            class={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold ${
              tab.value === t
                ? "border-primary/50 bg-primary/10 text-primary"
                : "border-border bg-background text-muted-foreground hover:bg-accent"
            }`}
          >
            {t === "subscriptions" ? <Users width="1em" height="1em" /> : t === "plans" ? <Tag width="1em" height="1em" /> : <DollarSign width="1em" height="1em" />}
            {t === "subscriptions"
              ? `Assinaturas (${subs.value.length})`
              : t === "plans"
                ? `Planos (${plans.value.length})`
                : `Faturas (${invoices.value.length})`}
          </button>
        ))}
      </div>

      {tab.value === "plans" && <PlansTab />}
      {tab.value === "subscriptions" && <SubsTab />}
      {tab.value === "invoices" && <InvoicesTab />}

      {creatingPlan.value && <PlanModal onClose={() => (creatingPlan.value = false)} />}
      {editingPlan.value && <PlanModal plan={editingPlan.value} onClose={() => (editingPlan.value = null)} />}
      {editingSub.value && <SubModal sub={editingSub.value} onClose={() => (editingSub.value = null)} />}
    </div>
  );
}

function PlansTab() {
  return (
    <div class="space-y-2">
      <div class="flex items-center justify-between">
        <p class="text-sm text-muted-foreground">CatÃ¡logo de planos disponÃ­veis.</p>
        <button
          type="button"
          onClick={() => (creatingPlan.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1em" height="1em" /> Novo plano
        </button>
      </div>
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {plans.value.map((p) => (
          <div
            key={p.id}
            class={`space-y-2 rounded-md border p-4 ${p.isActive ? "border-border bg-card/40" : "border-border bg-muted/30 opacity-60"}`}
          >
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0 flex-1">
                <h3 class="truncate text-sm font-bold">{p.name}</h3>
                <p class="font-mono text-[10px] text-muted-foreground">{p.slug}</p>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                {p.isDefault && (
                  <span class="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                    Default
                  </span>
                )}
                {!p.isActive && (
                  <span class="rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Inativo
                  </span>
                )}
              </div>
            </div>
            <div class="text-2xl font-bold tabular-nums text-foreground">
              {p.priceCents === 0 ? (
                <span class="text-emerald-600 dark:text-emerald-400">GrÃ¡tis</span>
              ) : (
                brl(p.priceCents)
              )}
              {p.priceCents > 0 && <span class="text-xs font-normal text-muted-foreground">/mÃªs</span>}
            </div>
            {p.description && (
              <p class="line-clamp-2 text-[11px] text-muted-foreground">{p.description}</p>
            )}
            <p class="text-[10px] text-muted-foreground">CobranÃ§a dia {p.defaultBillingDay}</p>
            <div class="flex justify-end gap-1 pt-1">
              <button
                type="button"
                onClick={() => (editingPlan.value = p)}
                title="Editar"
                aria-label="Editar plano"
                class="rounded-md border border-border bg-background p-1.5 text-xs hover:bg-accent"
              >
                <Edit width="0.9em" height="0.9em" />
              </button>
              {!p.isDefault && p.isActive && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!confirm(`Desativar "${p.name}"?`)) return;
                    await callApi(`/api/manager/billing?planId=${p.id}`, { method: "DELETE" });
                    await load();
                  }}
                  title="Desativar"
                  aria-label="Desativar"
                  class="rounded-md border border-red-500/40 bg-red-500/5 p-1.5 text-xs text-red-600 hover:bg-red-500/10"
                >
                  <Trash2 width="0.9em" height="0.9em" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubsTab() {
  const q = search.value.toLowerCase().trim();
  const fs = filterStatus.value;
  const visible = useMemo(() => {
    return subs.value.filter((s) => {
      if (fs !== "all" && s.status !== fs) return false;
      if (q && !`${s.userEmail} ${s.userName} ${s.planName}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [subs.value, q, fs]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { active: 0, cancelled: 0, paused: 0, past_due: 0 };
    for (const s of subs.value) c[s.status] = (c[s.status] ?? 0) + 1;
    return c;
  }, [subs.value]);
  return (
    <div class="space-y-3">
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar email, nome, planoâ€¦"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        {(["all", "active", "cancelled", "past_due", "paused"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => (filterStatus.value = s)}
            class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
              fs === s
                ? "border-primary/50 bg-primary/10 text-primary"
                : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
            }`}
          >
            {s === "all" ? "Todas" : s === "active" ? "Ativas" : s === "cancelled" ? "Canceladas" : s === "past_due" ? "Atrasadas" : "Pausadas"}
            {s !== "all" && (
              <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums">{counts[s] ?? 0}</span>
            )}
          </button>
        ))}
      </div>
      <p class="px-1 text-xs text-muted-foreground">
        {visible.length} de {subs.value.length}
      </p>
      <div class="overflow-hidden rounded-md border border-border bg-card/40">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <th class="px-3 py-2">UsuÃ¡rio</th>
              <th class="px-3 py-2">Plano</th>
              <th class="px-3 py-2 text-right">Valor</th>
              <th class="px-3 py-2">Status</th>
              <th class="px-3 py-2">PrÃ³x. fatura</th>
              <th class="px-3 py-2 text-right">AÃ§Ãµes</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.id} class="border-b border-border/60 last:border-0 hover:bg-accent/30">
                <td class="px-3 py-2">
                  <div class="font-semibold">{s.userName}</div>
                  <div class="font-mono text-[11px] text-muted-foreground">{s.userEmail}</div>
                </td>
                <td class="px-3 py-2">
                  <span class="rounded bg-violet-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700 dark:text-violet-300">
                    {s.planName}
                  </span>
                </td>
                <td class="px-3 py-2 text-right tabular-nums font-bold">
                  {s.priceCents === 0 ? "â€”" : brl(s.priceCents)}
                </td>
                <td class="px-3 py-2">
                  <SubStatusBadge status={s.status} />
                </td>
                <td class="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                  {s.nextInvoiceAt ? new Date(s.nextInvoiceAt).toLocaleDateString("pt-BR") : "â€”"}
                </td>
                <td class="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => (editingSub.value = s)}
                    title="Editar"
                    aria-label="Editar assinatura"
                    class="rounded-md border border-border bg-background p-1.5 text-xs hover:bg-accent"
                  >
                    <Edit width="0.9em" height="0.9em" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubStatusBadge({ status }: { status: string }) {
  if (status === "active")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 width="0.85em" height="0.85em" /> Ativa
      </span>
    );
  if (status === "past_due")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">
        <AlertCircle width="0.85em" height="0.85em" /> Atrasada
      </span>
    );
  if (status === "cancelled")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <Ban width="0.85em" height="0.85em" /> Cancelada
      </span>
    );
  return (
    <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
      Pausada
    </span>
  );
}

function InvoicesTab() {
  const q = search.value.toLowerCase().trim();
  const fs = filterStatus.value;
  const visible = useMemo(() => {
    return invoices.value.filter((i) => {
      if (fs !== "all" && i.status !== fs) return false;
      if (q && !`${i.userEmail} ${i.userName}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [invoices.value, q, fs]);
  return (
    <div class="space-y-3">
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar email, nomeâ€¦"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        {(["all", "pending", "paid", "overdue", "cancelled"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => (filterStatus.value = s)}
            class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
              fs === s
                ? "border-primary/50 bg-primary/10 text-primary"
                : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
            }`}
          >
            {s === "all" ? "Todas" : s === "pending" ? "Pendentes" : s === "paid" ? "Pagas" : s === "overdue" ? "Atrasadas" : "Canceladas"}
          </button>
        ))}
      </div>
      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma fatura.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">UsuÃ¡rio</th>
                <th class="px-3 py-2">Vencimento</th>
                <th class="px-3 py-2 text-right">Valor</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2">Pago em</th>
                <th class="px-3 py-2 text-right">AÃ§Ãµes</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((inv) => (
                <tr key={inv.id} class="border-b border-border/60 last:border-0 hover:bg-accent/30">
                  <td class="px-3 py-2">
                    <div class="font-semibold">{inv.userName}</div>
                    <div class="font-mono text-[11px] text-muted-foreground">{inv.userEmail}</div>
                  </td>
                  <td class="px-3 py-2 font-mono text-[11px]">{new Date(inv.dueAt).toLocaleDateString("pt-BR")}</td>
                  <td class="px-3 py-2 text-right tabular-nums font-bold">{brl(inv.amountCents)}</td>
                  <td class="px-3 py-2">
                    <InvoiceStatusBadge status={inv.status} />
                  </td>
                  <td class="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                    {inv.paidAt ? `${new Date(inv.paidAt).toLocaleDateString("pt-BR")} (${inv.paidByMethod ?? "â€”"})` : "â€”"}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {inv.status === "pending" || inv.status === "overdue" ? (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!confirm(`Marcar fatura como paga?`)) return;
                          await callApi(`/api/manager/billing?invoiceId=${inv.id}`, {
                            method: "PATCH",
                            body: JSON.stringify({ markPaid: true, method: "manual" })
                          });
                          await load();
                        }}
                        class="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
                      >
                        Marcar paga
                      </button>
                    ) : (
                      <span class="text-[10px] text-muted-foreground">â€”</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvoiceStatusBadge({ status }: { status: string }) {
  if (status === "paid")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 width="0.85em" height="0.85em" /> Paga
      </span>
    );
  if (status === "overdue")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">
        <AlertCircle width="0.85em" height="0.85em" /> Atrasada
      </span>
    );
  if (status === "cancelled" || status === "refunded")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {status === "refunded" ? "Reembolsada" : "Cancelada"}
      </span>
    );
  return (
    <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
      <Clock width="0.85em" height="0.85em" /> Pendente
    </span>
  );
}

function PlanModal({ plan, onClose }: { plan?: Plan; onClose: () => void }) {
  const [name, setName] = useState(plan?.name ?? "");
  const [slug, setSlug] = useState(plan?.slug ?? "");
  const [description, setDescription] = useState(plan?.description ?? "");
  const [priceReais, setPriceReais] = useState(plan ? (plan.priceCents / 100).toFixed(2) : "");
  const [billingDay, setBillingDay] = useState(String(plan?.defaultBillingDay ?? 5));
  const [isActive, setIsActive] = useState(plan?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const isEdit = !!plan;

  async function go() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const priceCents = Math.round((Number(priceReais) || 0) * 100);
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        priceCents,
        defaultBillingDay: Number(billingDay) || 5,
        isActive
      };
      if (isEdit) {
        await callApi(`/api/manager/billing?planId=${plan!.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload)
        });
      } else {
        await callApi("/api/manager/billing?action=create-plan", {
          method: "POST",
          body: JSON.stringify({ ...payload, slug: slug.trim() })
        });
      }
      onClose();
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={isEdit ? `Editar: ${plan!.name}` : "Novo plano"} onClose={onClose}>
      <Field label="Nome *">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          autoFocus
          aria-label="Nome"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      {!isEdit && (
        <Field label="Slug (identificador Ãºnico)">
          <input
            type="text"
            value={slug}
            onInput={(e) => setSlug((e.target as HTMLInputElement).value.toLowerCase().replace(/\s+/g, "-"))}
            placeholder="basico, pro, premium"
            aria-label="Slug"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm outline-none focus:border-primary"
          />
        </Field>
      )}
      <Field label="DescriÃ§Ã£o">
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          aria-label="DescriÃ§Ã£o"
          placeholder="Opcional"
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <div class="grid grid-cols-2 gap-2">
        <Field label="PreÃ§o (R$ por mÃªs)">
          <input
            type="number"
            step="0.01"
            min="0"
            value={priceReais}
            onInput={(e) => setPriceReais((e.target as HTMLInputElement).value)}
            placeholder="29.90"
            aria-label="PreÃ§o"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Dia de cobranÃ§a (1-28)">
          <input
            type="number"
            min="1"
            max="28"
            value={billingDay}
            onInput={(e) => setBillingDay((e.target as HTMLInputElement).value)}
            aria-label="Dia"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
      </div>
      <label class="flex items-center gap-2 text-xs">
        <Switch
          checked={isActive}
          onCheckedChange={(v) => setIsActive(v)}
        />
        Plano ativo (disponÃ­vel para assinatura)
      </label>
      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={busy || !name.trim() || (!isEdit && !slug.trim())}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : isEdit ? <Edit width="1em" height="1em" /> : <Plus width="1em" height="1em" />}
          {isEdit ? "Salvar" : "Criar"}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function SubModal({ sub, onClose }: { sub: Subscription; onClose: () => void }) {
  const [planId, setPlanId] = useState(sub.planId);
  const [billingDay, setBillingDay] = useState(String(sub.billingDayOfMonth));
  const [status, setStatus] = useState(sub.status);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await callApi(`/api/manager/billing?subId=${sub.id}`, {
        method: "PATCH",
        body: JSON.stringify({ planId, billingDayOfMonth: Number(billingDay), status })
      });
      onClose();
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }

  async function genInvoice() {
    if (!confirm(`Gerar fatura agora pra ${sub.userName}?`)) return;
    setBusy(true);
    try {
      await callApi(`/api/manager/billing?action=generate-invoice&subId=${sub.id}`, {
        method: "POST"
      });
      onClose();
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Assinatura: ${sub.userName}`} onClose={onClose}>
      <p class="text-xs text-muted-foreground font-mono">{sub.userEmail}</p>
      <Field label="Plano">
        <select
          value={planId}
          onChange={(e) => setPlanId((e.target as HTMLSelectElement).value)}
          aria-label="Plano"
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
        >
          {plans.value.filter((p) => p.isActive).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.priceCents === 0 ? "GrÃ¡tis" : brl(p.priceCents)})
            </option>
          ))}
        </select>
      </Field>
      <Field label="Dia de cobranÃ§a (1-28)">
        <input
          type="number"
          min="1"
          max="28"
          value={billingDay}
          onInput={(e) => setBillingDay((e.target as HTMLInputElement).value)}
          aria-label="Dia"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Status">
        <select
          value={status}
          onChange={(e) => setStatus((e.target as HTMLSelectElement).value)}
          aria-label="Status"
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
        >
          <option value="active">Ativa</option>
          <option value="paused">Pausada</option>
          <option value="past_due">Atrasada</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </Field>
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
        <p class="text-[11px] text-muted-foreground">
          Gerar fatura manualmente â€” Ãºtil pra adiantar uma cobranÃ§a ou regularizar
          mudanÃ§a de plano.
        </p>
        <button
          type="button"
          onClick={genInvoice}
          disabled={busy}
          class="mt-2 inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/15 px-3 py-1 text-[11px] font-bold text-violet-700 hover:bg-violet-500/25 disabled:opacity-50 dark:text-violet-300"
        >
          <DollarSign width="0.9em" height="0.9em" /> Gerar fatura agora
        </button>
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Edit width="1em" height="1em" />}
          Salvar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: any }) {
  // ESC fecha; click no backdrop NÃƒO fecha. [[feedback-modal-no-click-outside-close]]
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
    >
      <div
        class="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <CreditCard width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
function ModalFooter({ children }: { children: any }) {
  return (
    <div class="sticky bottom-0 -mx-4 -mb-4 mt-2 flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
      {children}
    </div>
  );
}
